import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha, hoy } from '../utils/formato';
import { categoriaDelAnimal, estructuraDelHato, gananciaDiaria,
         partosPorAnimal, pesajesDe, loQueToca, porKilo } from '../utils/hato';

// El ganado.
//
// Lo que se quiere saber de un animal no es lo que costó, sino lo que costó EN
// TOTAL: la compra más las vacunas, los tratamientos y todo lo que se le fue
// haciendo. Un novillo comprado en 3.000.000 y vendido en 3.400.000 parece que
// dejó 400.000, hasta que se suman 600.000 de veterinario.
//
// Por eso la tabla enseña el costo acumulado al lado del precio de compra, y al
// vender dice el resultado de verdad.

const TIPOS_EVENTO = [
  ['pesaje',      '⚖️ Pesaje'],
  ['vacuna',      '💉 Vacuna'],
  ['tratamiento', '🩺 Tratamiento'],
  ['parto',       '🐄 Parto'],
  ['traslado',    '🚚 Traslado'],
  ['otro',        '📝 Otro'],
];

function Ganado() {
  const [animales, setAnimales] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [verBajas, setVerBajas] = useState(false);

  const [nuevo, setNuevo] = useState(null);
  const [vendiendo, setVendiendo] = useState(null);
  const [anotando, setAnotando] = useState(null);   // evento
  const [abierto, setAbierto] = useState(null);      // ficha desplegada
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    Promise.all([
      axios.get('/api/ganado/resumen').then(r => setAnimales(r.data || [])),
      axios.get('/api/eventos-ganado').then(r => setEventos(r.data || [])),
    ]).catch(() => {}).finally(() => setCargando(false));
  }, []);

  useEffect(cargar, [cargar]);

  const activos = animales.filter(a => a.estado === 'activo');
  const salidos = animales.filter(a => a.estado !== 'activo');
  const visibles = verBajas ? animales : activos;

  const invertido = activos.reduce((s, a) => s + (a.costo_total || 0), 0);
  const vendidos = animales.filter(a => a.estado === 'vendido');
  const resultado = vendidos.reduce((s, a) => s + (a.resultado || 0), 0);

  const eventosDe = (id) => eventos.filter(e => e.ganado_id === id);

  // La categoría de cada animal se calcula, no se guarda: una columna con la
  // palabra «novilla» escrita adentro envejece sola el día que la vaca pare, y
  // hay que acordarse de cambiarla. Los partos salen de los eventos que esta
  // misma pantalla ya tiene cargados, así que no cuesta una consulta más.
  const partos = partosPorAnimal(eventos);
  const conCategoria = (a) => categoriaDelAnimal({ ...a, partos: partos.get(a.id) || 0 });
  const estructura = estructuraDelHato(
    animales.map(a => ({ ...a, partos: partos.get(a.id) || 0 })));

  const toca = loQueToca(eventos, animales);
  const pendientes = toca.vencidas.length + toca.pronto.length;

  const crear = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await axios.post('/api/ganado', {
        ...nuevo,
        precio_compra: parseFloat(nuevo.precio_compra) || 0,
        peso_ingreso: nuevo.peso_ingreso ? parseFloat(nuevo.peso_ingreso) : null,
      });
      setAviso({ tipo: 'bueno', texto: `${nuevo.arete || nuevo.nombre} agregado al hato` });
      setNuevo(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar');
    }
    setGuardando(false);
  };

  const vender = async (e) => {
    e.preventDefault();
    const precio = parseFloat(vendiendo.precio_venta);
    if (!(precio > 0)) return alert('El precio debe ser mayor que cero');

    setGuardando(true);
    try {
      const { data } = await axios.post(`/api/ganado/${vendiendo.animal.id}/vender`, {
        precio_venta: precio,
        kilos_salida: parseFloat(vendiendo.kilos_salida) || null,
        precio_kilo: parseFloat(vendiendo.precio_kilo) || null,
        comprador: vendiendo.comprador || null,
        fecha_salida: vendiendo.fecha,
        nota: vendiendo.nota || null,
      });
      const gano = data.resultado >= 0;
      setAviso({
        tipo: gano ? 'bueno' : 'ojo',
        texto: `${vendiendo.animal.arete || vendiendo.animal.nombre}: ` +
               `vendido en ${plata(precio)}. Costó ${plata(data.precio_compra)} ` +
               `más ${plata(data.gastado_en_eventos)} de cuidados. ` +
               `${gano ? 'Dejó' : 'Perdió'} ${plata(Math.abs(data.resultado))}.`,
      });
      setVendiendo(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo registrar la venta');
    }
    setGuardando(false);
  };

  const darDeBaja = async (a) => {
    if (!window.confirm(
      `¿Dar de baja a ${a.arete || a.nombre}?\n\n` +
      'Es para animales que se murieron o se perdieron. Si lo vendiste, usa ' +
      '«Vender» en su lugar: uno deja plata y el otro se la lleva, y hay que ' +
      'poder distinguirlos en las cuentas.'
    )) return;
    try {
      await axios.post(`/api/ganado/${a.id}/baja`, { fecha_salida: hoy() });
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo dar de baja');
    }
  };

  const anotarEvento = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await axios.post('/api/eventos-ganado', {
        ganado_id: anotando.animal.id,
        tipo: anotando.tipo,
        fecha: anotando.fecha,
        valor: anotando.valor ? parseFloat(anotando.valor) : null,
        costo: parseFloat(anotando.costo) || 0,
        proxima_fecha: anotando.proxima_fecha || null,
        nota: anotando.nota || null,
      });
      setAnotando(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar');
    }
    setGuardando(false);
  };

  return (
    <>
      <div className="encabezado">
        <h1>Ganado</h1>
        <p>El hato, lo que costó cada animal y lo que dejó</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--ganado)' }}>
          <div className="rotulo">🐄 En el hato</div>
          <div className="valor cifra">{activos.length}</div>
          <div className="pie">{activos.length === 1 ? 'cabeza activa' : 'cabezas activas'}</div>
        </div>
        <div className="dato" style={{ '--color': 'var(--ganado)' }}>
          <div className="rotulo">💰 Invertido en el hato</div>
          <div className="valor cifra">{plata(invertido)}</div>
          <div className="pie">compra más cuidados</div>
        </div>
        <div className="dato" style={{ '--color': resultado >= 0 ? 'var(--bueno)' : 'var(--malo)' }}>
          <div className="rotulo">📊 De lo vendido</div>
          <div className="valor cifra">{plata(resultado)}</div>
          <div className="pie">
            {vendidos.length} {vendidos.length === 1 ? 'vendido' : 'vendidos'}
            {vendidos.length > 0 && (resultado >= 0 ? ' · ganancia' : ' · pérdida')}
          </div>
        </div>
      </div>

      {/* Lo que falta hacer. Va ARRIBA de todo lo demás: es lo único de esta
          pantalla que pide una acción hoy; el resto es consulta. Y si no hay
          nada pendiente no aparece — un recuadro que siempre está, diciendo
          «todo al día», se vuelve parte del fondo y deja de leerse. */}
      {(pendientes > 0 || toca.adelante.length > 0) && (
        <div className="bloque">
          <h2>
            🗓️ Qué toca
            {/* El conteo dice «pendientes» de lo que pide acción ahora, pero
                debajo se enseña también lo del mes. Sin nombrar esa diferencia,
                quien lee cuenta tres renglones bajo un rótulo que dice dos y
                deja de creerle al número. */}
            <span className="contador">
              {pendientes > 0
                ? `${pendientes} ${pendientes === 1 ? 'pendiente' : 'pendientes'}`
                : 'nada esta semana'}
              {toca.adelante.length > 0 && ` · ${toca.adelante.length} más adelante`}
            </span>
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[...toca.vencidas, ...toca.pronto, ...toca.adelante].map((t, i) => {
              const etiqueta = (TIPOS_EVENTO.find(([c]) => c === t.tipo) || [null, t.tipo])[1];
              const vencida = t.dias < 0;
              const esta = t.dias >= 0 && t.dias <= 7;
              return (
                <div key={`${t.animal.id}-${t.tipo}-${i}`} style={{
                  display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 10,
                  padding: '8px 12px', borderRadius: 6,
                  borderLeft: `3px solid ${vencida ? 'var(--malo)' : esta ? 'var(--ojo)' : 'var(--linea)'}`,
                  background: 'var(--hondo)',
                }}>
                  <span className="recio cifra" style={{ minWidth: 62 }}>
                    {t.arete || '—'}
                  </span>
                  <span style={{ minWidth: 110 }}>{t.nombre || ''}</span>
                  <span>{etiqueta}</span>
                  <span style={{
                    marginLeft: 'auto', fontSize: 13,
                    color: vencida ? 'var(--malo)' : 'var(--tinta-tenue)',
                    fontWeight: vencida ? 700 : 400,
                  }}>
                    {/* En días, no en fecha. «Hace 12 días» pesa; «13 de agosto»
                        hay que restarlo de cabeza para saber si urge. */}
                    {vencida ? `atrasado ${-t.dias} ${-t.dias === 1 ? 'día' : 'días'}`
                      : t.dias === 0 ? 'hoy'
                      : t.dias === 1 ? 'mañana'
                      : `en ${t.dias} días`}
                    <span style={{ color: 'var(--tinta-tenue)', fontWeight: 400 }}>
                      {' · '}{fecha(t.proxima)}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* La estructura del hato: cuántas vacas, cuántas novillas, cuántas
          crías. Es lo primero que responde cualquiera al que le preguntan qué
          tiene, y hasta ahora la pantalla solo sabía decir «40 cabezas». */}
      {estructura.length > 0 && (
        <div className="bloque">
          <h2>
            📋 Cómo está compuesto
            <span className="contador">se calcula de la edad y los partos</span>
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {estructura.map(c => (
              <div key={c.clave} style={{
                border: '1px solid var(--linea)', borderRadius: 6,
                padding: '8px 14px', minWidth: 96,
              }}>
                <div className="cifra recio" style={{ fontSize: 22, lineHeight: 1.1 }}>
                  {c.cuantos}
                </div>
                <div style={{ fontSize: 12, color: 'var(--tinta-tenue)' }}>
                  {c.cuantos === 1 ? c.nombre : c.nombre + 's'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bloque">
        <h2>
          🐄 El hato
          <span className="contador">
            {activos.length} activos{salidos.length > 0 && ` · ${salidos.length} salidos`}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {salidos.length > 0 && (
              <button className="boton chico suave" onClick={() => setVerBajas(!verBajas)}>
                {verBajas ? 'Solo activos' : 'Ver salidos'}
              </button>
            )}
            <button className="boton chico"
                    onClick={() => setNuevo({ arete: '', nombre: '', sexo: 'H', raza: '',
                                              origen: 'comprado', fecha_ingreso: hoy(),
                                              fecha_nacimiento: '',
                                              precio_compra: '', vendedor: '', peso_ingreso: '' })}>
              ➕ Agregar animal
            </button>
          </span>
        </h2>

        {cargando ? (
          <div className="vacio">Cargando…</div>
        ) : visibles.length === 0 ? (
          <div className="vacio">
            Todavía no hay animales registrados. Agrega el primero para llevar
            la cuenta de lo que cuesta y lo que deja.
          </div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Arete</th>
                  <th>Nombre</th>
                  <th>Categoría</th>
                  <th>Raza</th>
                  <th>Origen</th>
                  <th style={{ textAlign: 'right' }}>Compra</th>
                  <th style={{ textAlign: 'right' }}>Cuidados</th>
                  <th style={{ textAlign: 'right' }}>Costo total</th>
                  <th style={{ textAlign: 'right' }}>Resultado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibles.map(a => (
                  <React.Fragment key={a.id}>
                    <tr style={{ opacity: a.estado === 'activo' ? 1 : 0.55 }}>
                      <td className="recio cifra">{a.arete || '—'}</td>
                      <td>
                        {a.nombre || '—'}
                        {a.estado !== 'activo' && (
                          <div>
                            <span className={'pinta ' + (a.estado === 'vendido' ? 'gris' : 'malo')}>
                              {a.estado === 'vendido' ? 'vendido' : 'baja'}
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const c = conCategoria(a);
                          const falta = c.clave === 'sin_edad' || c.clave === 'sin_datos';
                          return (
                            <>
                              <span className={'pinta ' + (falta ? 'gris' : '')}>{c.nombre}</span>
                              {/* La edad al lado, porque «novilla» sin más no
                                  distingue a la de 13 meses de la de 4 años, y
                                  esa diferencia decide si ya debería estar
                                  preñada. */}
                              {c.meses != null && (
                                <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                  {c.meses < 24
                                    ? `${Math.floor(c.meses)} meses`
                                    : `${Math.floor(c.meses / 12)} años`}
                                  {c.partos > 0 && ` · ${c.partos} ${c.partos === 1 ? 'parto' : 'partos'}`}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td>{a.raza || '—'}</td>
                      <td>
                        <span className="pinta gris">
                          {a.origen === 'nacido' ? 'nacido aquí' : 'comprado'}
                        </span>
                      </td>
                      <td className="num">{plata(a.precio_compra)}</td>
                      <td className="num" style={{ color: a.gastado_en_eventos > 0 ? 'var(--ojo)' : 'var(--tinta-tenue)' }}>
                        {a.gastado_en_eventos > 0 ? plata(a.gastado_en_eventos) : '—'}
                        {a.eventos > 0 && (
                          <div style={{ fontSize: 11 }}>{a.eventos} anotaciones</div>
                        )}
                      </td>
                      <td className="num recio">{plata(a.costo_total)}</td>
                      <td className="num recio"
                          style={{ color: a.resultado == null ? 'var(--tinta-tenue)'
                                 : a.resultado >= 0 ? 'var(--bueno)' : 'var(--malo)' }}>
                        {a.resultado == null ? '—' : plata(a.resultado)}
                        {a.estado === 'vendido' && (
                          <div style={{ fontSize: 11, fontWeight: 400 }}>
                            vendido en {plata(a.precio_venta)}
                          </div>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="boton chico suave" style={{ marginRight: 5 }}
                                onClick={() => setAbierto(abierto === a.id ? null : a.id)}>
                          {abierto === a.id ? '▲' : '▼'}
                        </button>
                        {a.estado === 'activo' && (
                          <>
                            <button className="boton chico suave" style={{ marginRight: 5 }}
                                    onClick={() => setAnotando({ animal: a, tipo: 'vacuna',
                                                                 fecha: hoy(), valor: '',
                                                                 costo: '', proxima_fecha: '',
                                                                 nota: '' })}>
                              Anotar
                            </button>
                            <button className="boton chico"
                                    onClick={() => setVendiendo({ animal: a, fecha: hoy(),
                                                                  precio_venta: '', kilos_salida: '',
                                                                  precio_kilo: '',
                                                                  comprador: '', nota: '' })}>
                              Vender
                            </button>
                          </>
                        )}
                      </td>
                    </tr>

                    {/* La historia del animal */}
                    {abierto === a.id && (
                      <tr>
                        <td colSpan={10} style={{ background: 'var(--hondo)', padding: '14px 18px' }}>
                          {/* Cómo viene creciendo. Los pesajes ya estaban
                              anotados; lo único que faltaba era la resta.
                              Se dice en gramos por día porque así se habla en
                              la finca: «me está dando 650 gramos». */}
                          {/* La venta en pie. Va arriba del todo en la ficha
                              del vendido porque es la única pregunta que queda
                              por hacerse de un animal que ya salió: ¿me
                              pagaron bien? */}
                          {(() => {
                            const k = porKilo(a);
                            if (!k || (k.compraKilo === null && k.ventaKilo === null)) return null;
                            return (
                              <div style={{
                                display: 'flex', flexWrap: 'wrap', gap: 18,
                                paddingBottom: 12, marginBottom: 12,
                                borderBottom: '1px solid var(--linea)',
                              }}>
                                {k.compraKilo !== null && (
                                  <div>
                                    <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                      Compró el kilo a
                                    </div>
                                    <div className="cifra recio">{plata(k.compraKilo)}</div>
                                    <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                      {k.kilosEntrada} kg al entrar
                                    </div>
                                  </div>
                                )}
                                {k.ventaKilo !== null && (
                                  <div>
                                    <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                      Vendió el kilo a
                                    </div>
                                    <div className="cifra recio">{plata(k.ventaKilo)}</div>
                                    <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                      {k.kilosSalida} kg al salir
                                    </div>
                                  </div>
                                )}
                                {/* El caso que el total de la venta esconde:
                                    engordó bien pero el mercado se le cayó. */}
                                {k.diferenciaKilo !== null && (
                                  <div>
                                    <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                      Diferencia por kilo
                                    </div>
                                    <div className="cifra recio"
                                         style={{ color: k.diferenciaKilo >= 0 ? 'var(--bueno)' : 'var(--malo)' }}>
                                      {/* El signo va delante del peso, no
                                          entre el peso y el número: `plata()`
                                          de un negativo escribe «$-1.296», que
                                          se lee como un error de tecleo. */}
                                      {k.diferenciaKilo >= 0 ? '+' : '−'}
                                      {plata(Math.abs(k.diferenciaKilo))}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                      {k.kilosGanados !== null
                                        ? `engordó ${k.kilosGanados} kg`
                                        : 'entre comprar y vender'}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* Vender, también desde aquí.
                              En la tabla ese botón queda de décimo y se sale de
                              la pantalla; en el teléfono hay que arrastrar a
                              ciegas para encontrarlo. Y es el botón que mueve
                              millones. Aquí está donde de verdad se decide:
                              después de mirar lo que costó y cómo viene
                              creciendo. */}
                          {a.estado === 'activo' && (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                                          paddingBottom: 12, marginBottom: 12,
                                          borderBottom: '1px solid var(--linea)' }}>
                              <button className="boton chico"
                                      onClick={() => setVendiendo({ animal: a, fecha: hoy(),
                                                                    precio_venta: '', kilos_salida: '',
                                                                    precio_kilo: '',
                                                                    comprador: '', nota: '' })}>
                                💵 Vender este animal
                              </button>
                              <button className="boton chico suave"
                                      onClick={() => setAnotando({ animal: a, tipo: 'pesaje',
                                                                   fecha: hoy(), valor: '',
                                                                   costo: '', proxima_fecha: '',
                                                                   nota: '' })}>
                                ⚖️ Anotar un pesaje
                              </button>
                            </div>
                          )}

                          {(() => {
                            const g = gananciaDiaria(a, pesajesDe(eventos, a.id));
                            if (!g) return null;
                            const flojo = g.gramosUltimoTramo != null && g.gramosUltimoTramo < 200;
                            return (
                              <div style={{
                                display: 'flex', flexWrap: 'wrap', gap: 18,
                                paddingBottom: 12, marginBottom: 12,
                                borderBottom: '1px solid var(--linea)',
                              }}>
                                <div>
                                  <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                    Peso hoy
                                  </div>
                                  <div className="cifra recio">{g.pesoActual} kg</div>
                                  <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                    {g.diasSinPesar === 0 ? 'pesado hoy'
                                      : `hace ${g.diasSinPesar} días`}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                    Gana al día
                                  </div>
                                  <div className="cifra recio"
                                       style={{ color: g.gramosPorDia < 0 ? 'var(--malo)' : 'inherit' }}>
                                    {g.gramosPorDia} g
                                  </div>
                                  <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                    {g.kilosGanados > 0 ? '+' : ''}{g.kilosGanados} kg en total
                                  </div>
                                </div>
                                {/* El último tramo va aparte porque es el que
                                    avisa: el que venía bien y se frenó queda
                                    disimulado en el promedio de toda la vida. */}
                                {g.gramosUltimoTramo != null && g.pesajes > 2 && (
                                  <div>
                                    <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                      Desde el pesaje anterior
                                    </div>
                                    <div className="cifra recio"
                                         style={{ color: flojo ? 'var(--malo)' : 'var(--bueno)' }}>
                                      {g.gramosUltimoTramo} g
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                      {flojo ? 'viene flojo' : 'va bien'}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          <div style={{ fontSize: 13, color: 'var(--tinta-media)', marginBottom: 10 }}>
                            {a.sexo === 'H' ? '♀ Hembra' : '♂ Macho'}
                            {a.fecha_nacimiento && ` · nació el ${fecha(a.fecha_nacimiento)}`}
                            {a.fecha_ingreso && ` · entró el ${fecha(a.fecha_ingreso)}`}
                            {a.peso_ingreso && ` · pesó ${a.peso_ingreso} kg al entrar`}
                            {a.vendedor && ` · comprado a ${a.vendedor}`}
                            {a.comprador && ` · vendido a ${a.comprador}`}
                            {a.nota && ` · ${a.nota}`}
                          </div>

                          {eventosDe(a.id).length === 0 ? (
                            <div style={{ color: 'var(--tinta-tenue)', fontSize: 13 }}>
                              Sin anotaciones todavía.
                            </div>
                          ) : (
                            <table className="datos" style={{ background: 'var(--tarjeta)' }}>
                              <thead>
                                <tr>
                                  <th>Fecha</th><th>Qué</th>
                                  <th style={{ textAlign: 'right' }}>Valor</th>
                                  <th style={{ textAlign: 'right' }}>Costó</th>
                                  <th>Nota</th>
                                </tr>
                              </thead>
                              <tbody>
                                {eventosDe(a.id).map(ev => (
                                  <tr key={ev.id}>
                                    <td>{fecha(ev.fecha)}</td>
                                    <td>{(TIPOS_EVENTO.find(t => t[0] === ev.tipo) || [, ev.tipo])[1]}</td>
                                    <td className="num">{ev.valor ?? '—'}</td>
                                    <td className="num">{ev.costo > 0 ? plata(ev.costo) : '—'}</td>
                                    <td style={{ color: 'var(--tinta-tenue)' }}>{ev.nota || ''}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}

                          {a.estado === 'activo' && (
                            <button className="boton chico suave" style={{ marginTop: 10 }}
                                    onClick={() => darDeBaja(a)}>
                              Dar de baja (murió o se perdió)
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Agregar animal */}
      {nuevo && (
        <div className="bloque">
          <h2>➕ Agregar animal</h2>
          <form className="cuerpo" onSubmit={crear}>
            <div className="campos">
              <label className="campo">
                <span>Arete o marca *</span>
                <input value={nuevo.arete} required placeholder="A-101"
                       onChange={e => setNuevo({ ...nuevo, arete: e.target.value })} />
              </label>
              <label className="campo">
                <span>Nombre</span>
                <input value={nuevo.nombre} placeholder="Como le dicen"
                       onChange={e => setNuevo({ ...nuevo, nombre: e.target.value })} />
              </label>
              <label className="campo">
                <span>Sexo</span>
                <select value={nuevo.sexo}
                        onChange={e => setNuevo({ ...nuevo, sexo: e.target.value })}>
                  <option value="H">Hembra</option>
                  <option value="M">Macho</option>
                </select>
              </label>
              <label className="campo">
                <span>Raza</span>
                <input value={nuevo.raza} placeholder="Holstein, Normando…"
                       onChange={e => setNuevo({ ...nuevo, raza: e.target.value })} />
              </label>
              <label className="campo">
                <span>De dónde salió</span>
                <select value={nuevo.origen}
                        onChange={e => {
                          const origen = e.target.value;
                          // El que nace aquí entra el día que nace: son la
                          // misma fecha y pedirla dos veces es hacer teclear lo
                          // mismo dos veces. Se rellena y se puede corregir.
                          setNuevo({
                            ...nuevo, origen,
                            fecha_nacimiento: origen === 'nacido' && !nuevo.fecha_nacimiento
                              ? nuevo.fecha_ingreso : nuevo.fecha_nacimiento,
                          });
                        }}>
                  <option value="comprado">Comprado</option>
                  <option value="nacido">Nació aquí</option>
                </select>
              </label>
              <label className="campo">
                <span>Fecha de entrada</span>
                <input type="date" value={nuevo.fecha_ingreso}
                       onChange={e => setNuevo({ ...nuevo, fecha_ingreso: e.target.value })} />
              </label>
              {/* La edad es la que decide la categoría: sin esta fecha, todo
                  animal sale como «Sin fecha de nacimiento» y la pantalla de
                  «Cómo está compuesto» no dice nada.
                  Va opcional porque del animal comprado a veces solo se sabe
                  que ya venía pariendo — ese caso lo resuelven los partos. */}
              <label className="campo">
                <span>Fecha de nacimiento</span>
                <input type="date" value={nuevo.fecha_nacimiento || ''}
                       max={hoy()}
                       onChange={e => setNuevo({ ...nuevo, fecha_nacimiento: e.target.value })} />
              </label>
              <label className="campo">
                <span>Peso al entrar (kg)</span>
                <input type="number" step="0.1" className="peso" value={nuevo.peso_ingreso}
                       placeholder="Opcional"
                       onChange={e => setNuevo({ ...nuevo, peso_ingreso: e.target.value })} />
              </label>

              {nuevo.origen === 'comprado' && (
                <>
                  <label className="campo">
                    <span>Precio de compra *</span>
                    <input type="number" step="1" className="peso" required
                           value={nuevo.precio_compra}
                           onChange={e => setNuevo({ ...nuevo, precio_compra: e.target.value })} />
                  </label>
                  <label className="campo">
                    <span>A quién se le compró</span>
                    <input value={nuevo.vendedor} placeholder="Feria, finca, persona"
                           onChange={e => setNuevo({ ...nuevo, vendedor: e.target.value })} />
                  </label>
                </>
              )}
            </div>

            {nuevo.origen === 'nacido' && (
              <div className="aviso" style={{ marginTop: 14, marginBottom: 0 }}>
                Un animal nacido aquí entra sin precio de compra. Lo que cueste
                criarlo se va sumando con las anotaciones de vacunas y cuidados.
              </div>
            )}

            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setNuevo(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Vender */}
      {vendiendo && (
        <div className="bloque">
          <h2>💵 Vender {vendiendo.animal.arete || vendiendo.animal.nombre}</h2>
          <form className="cuerpo" onSubmit={vender}>
            <div className="aviso">
              Costó <strong>{plata(vendiendo.animal.precio_compra)}</strong>
              {vendiendo.animal.gastado_en_eventos > 0 && (
                <> más <strong>{plata(vendiendo.animal.gastado_en_eventos)}</strong> de cuidados</>
              )}
              {' → '}
              <strong>{plata(vendiendo.animal.costo_total)}</strong> en total.
              {parseFloat(vendiendo.precio_venta) > 0 && (
                <div style={{ marginTop: 8, fontWeight: 600,
                              color: parseFloat(vendiendo.precio_venta) >= vendiendo.animal.costo_total
                                     ? 'var(--bueno)' : 'var(--malo)' }}>
                  A {plata(parseFloat(vendiendo.precio_venta))} el resultado sería{' '}
                  {plata(parseFloat(vendiendo.precio_venta) - vendiendo.animal.costo_total)}
                </div>
              )}
            </div>
            <div className="campos">
              {/* Los kilos y el precio del kilo van PRIMERO porque es el orden
                  de la negociación: se pesa el animal, se acuerda el kilo, y el
                  total sale de ahí. Pedir el total de primero obliga a hacer la
                  multiplicación de cabeza antes de poder escribir nada.

                  Son opcionales: hay ventas de bulto donde nadie pesó. Si se
                  dejan vacías, se escribe el total y ya. */}
              <label className="campo">
                <span>Kilos en pie</span>
                <input type="number" step="0.1" className="peso" autoFocus
                       placeholder="Opcional" value={vendiendo.kilos_salida}
                       onChange={e => {
                         const kilos = e.target.value;
                         const porK = parseFloat(vendiendo.precio_kilo);
                         setVendiendo({
                           ...vendiendo, kilos_salida: kilos,
                           precio_venta: kilos && porK > 0
                             ? String(Math.round(parseFloat(kilos) * porK))
                             : vendiendo.precio_venta,
                         });
                       }} />
              </label>
              <label className="campo">
                <span>Precio por kilo</span>
                <input type="number" step="1" className="peso"
                       placeholder="Opcional" value={vendiendo.precio_kilo}
                       onChange={e => {
                         const porK = e.target.value;
                         const kilos = parseFloat(vendiendo.kilos_salida);
                         setVendiendo({
                           ...vendiendo, precio_kilo: porK,
                           precio_venta: porK && kilos > 0
                             ? String(Math.round(parseFloat(porK) * kilos))
                             : vendiendo.precio_venta,
                         });
                       }} />
              </label>
              <label className="campo">
                <span>Precio de venta *</span>
                <input type="number" step="1" className="peso" required
                       value={vendiendo.precio_venta}
                       onChange={e => setVendiendo({ ...vendiendo, precio_venta: e.target.value })} />
              </label>
              <label className="campo">
                <span>A quién</span>
                <input value={vendiendo.comprador}
                       onChange={e => setVendiendo({ ...vendiendo, comprador: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input type="date" value={vendiendo.fecha}
                       onChange={e => setVendiendo({ ...vendiendo, fecha: e.target.value })} />
              </label>
            </div>
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setVendiendo(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Registrar la venta'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Anotar un evento */}
      {anotando && (
        <div className="bloque">
          <h2>📝 Anotar en {anotando.animal.arete || anotando.animal.nombre}</h2>
          <form className="cuerpo" onSubmit={anotarEvento}>
            <div className="campos">
              <label className="campo">
                <span>Qué pasó</span>
                <select value={anotando.tipo}
                        onChange={e => setAnotando({ ...anotando, tipo: e.target.value })}>
                  {TIPOS_EVENTO.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                </select>
              </label>
              <label className="campo">
                <span>{anotando.tipo === 'pesaje' ? 'Peso (kg)' : 'Valor'}</span>
                <input type="number" step="0.1" className="peso" value={anotando.valor}
                       placeholder="Opcional"
                       onChange={e => setAnotando({ ...anotando, valor: e.target.value })} />
              </label>
              <label className="campo">
                <span>Cuánto costó</span>
                <input type="number" step="1" className="peso" value={anotando.costo}
                       placeholder="0"
                       onChange={e => setAnotando({ ...anotando, costo: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input type="date" value={anotando.fecha}
                       onChange={e => setAnotando({ ...anotando, fecha: e.target.value })} />
              </label>
              {/* Va al lado de la fecha porque se piensan juntas: cuando uno
                  acaba de poner una vacuna es cuando sabe cuándo toca la otra.
                  Preguntarlo después, en otra pantalla, es garantizar que nunca
                  se llene. */}
              <label className="campo">
                <span>¿Cuándo toca otra vez?</span>
                <input type="date" value={anotando.proxima_fecha || ''}
                       onChange={e => setAnotando({ ...anotando, proxima_fecha: e.target.value })} />
              </label>
              <label className="campo">
                <span>Nota</span>
                <input value={anotando.nota} placeholder="Opcional"
                       onChange={e => setAnotando({ ...anotando, nota: e.target.value })} />
              </label>
            </div>
            <div className="aviso" style={{ marginTop: 14, marginBottom: 0 }}>
              Lo que cueste se suma al costo del animal. Es lo que hace que al
              venderlo la cuenta sea la de verdad y no solo la diferencia de precio.
            </div>
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setAnotando(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Anotar'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default Ganado;
